import ApplicationController from "./application_controller"
import Logger from '../lib/logger'
import { showToast } from '../utils/toast'

export default class extends ApplicationController {
  static targets = ["targetResults", "duplicateResults", "previewContainer", "targetInput", "duplicateInput"]
  static values = {
    previewUrl: String,
    duplicatesUrl: String,
    targetId: String
  }
  
  connect() {
    this.selectedTargetId = this.targetIdValue || null
    this.selectedDuplicateId = null
  }

  disconnect() {
    clearTimeout(this.searchTimeout)
  }

  selectTarget(event) {
    this.selectProvider(event, 'target')
  }

  selectDuplicate(event) {
    this.selectProvider(event, 'duplicate')
  }

  selectProvider(event, role) {
    const button = event.currentTarget
    const providerId = button.dataset.providerId
    const providerName = button.dataset.providerName
    const resolvedRole = role || button.dataset.role

    if (resolvedRole === 'target') {
      this.selectedTargetId = providerId
      if (this.hasTargetInputTarget && providerName) {
        this.targetInputTarget.value = providerName
      }
      
      const frame = document.getElementById("target_results")
      if (frame) frame.innerHTML = ""

      this.reloadDuplicatesFrame(this.selectedTargetId)
    } else {
      this.selectedDuplicateId = providerId
      if (this.hasDuplicateInputTarget && providerName) {
        this.duplicateInputTarget.value = providerName
      }
      
      const frame = document.getElementById("duplicate_results")
      if (frame) frame.innerHTML = ""
    }

    this.checkReadyToPreview()
  }

  selectPair(event) {
    const button = event.currentTarget
    this.selectedTargetId = button.dataset.targetId
    this.selectedDuplicateId = button.dataset.duplicateId
    
    if (this.hasTargetInputTarget) {
      this.targetInputTarget.value = ""
    }
    if (this.hasDuplicateInputTarget) {
      this.duplicateInputTarget.value = ""
    }

    this.reloadDuplicatesFrame(null)

    this.checkReadyToPreview()
  }

  checkReadyToPreview() {
    if (this.selectedTargetId && this.selectedDuplicateId) {
      if (this.selectedTargetId === this.selectedDuplicateId) {
        showToast("Target and Duplicate cannot be the same provider!", "error")
        return
      }
      this.loadPreview()
    }
  }

  loadPreview() {
    if (!this.hasPreviewContainerTarget) return
    
    this.previewContainerTarget.innerHTML = '<div class="spinner-border text-primary my-4"></div>'
    
    const url = `${this.previewUrlValue}?target_id=${this.selectedTargetId}&duplicate_id=${this.selectedDuplicateId}`

    this.safeFetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.text()
      })
      .then(html => {
        this.previewContainerTarget.innerHTML = html
        this.previewContainerTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
      .catch(error => {
        Logger.error("Error loading preview:", error, { controller: 'deduplication' })
        this.previewContainerTarget.innerHTML = '<div class="alert alert-danger">Error loading preview.</div>'
      })
  }

  // Helper method for the form's instant search
  submitSearch(event) {
    clearTimeout(this.searchTimeout)
    const input = event.currentTarget
    const form = input.closest('form')
    
    // If input is cleared, reset selection
    if (input.value.trim() === '') {
      const type = form.querySelector('input[name="type"]').value
      
      if (type === 'target') {
        this.selectedTargetId = null
        this.reloadDuplicatesFrame(null)
      } else if (type === 'duplicate') {
        this.selectedDuplicateId = null
      }
      
      const frameId = `${type}_results`
      const frame = document.getElementById(frameId)
      if (frame) frame.innerHTML = ""
      
      this.checkReadyToPreview()
      return
    }

    this.searchTimeout = setTimeout(() => {
      form.requestSubmit()
    }, 300)
  }



  startAnalysis(event) {
    const button = event.currentTarget
    button.classList.add('ai-score-button-active', 'disabled')
    button.style.pointerEvents = 'none'
    
    // Defer DOM mutation so the click event can bubble up to Turbo's listener
    // If we replace innerHTML immediately, the clicked element is detached and Turbo ignores it.
    setTimeout(() => {
      button.dataset.originalHtml = button.innerHTML
      button.innerHTML = '<span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Analyzing...'
    }, 0)
  }

  finishAnalysis(event) {
    const frame = event.currentTarget
    const failed = frame.querySelector('.text-danger') !== null
    const button = document.querySelector(`[data-turbo-frame="${frame.id}"]`)
    
    if (button && button.classList.contains('ai-score-button-active')) {
      if (failed) {
        button.classList.remove('disabled', 'ai-score-button-active')
        button.style.pointerEvents = 'auto'
        button.innerHTML = button.dataset.originalHtml
      } else {
        button.innerHTML = '<i class="fas fa-check me-1"></i> Analyzed'
        button.classList.replace('btn-outline-info', 'btn-outline-success')
      }
    }
  }

  swapRoles(event) {
    event.preventDefault()
    if (!this.selectedTargetId || !this.selectedDuplicateId) return

    const temp = this.selectedTargetId
    this.selectedTargetId = this.selectedDuplicateId
    this.selectedDuplicateId = temp
    
    this.loadPreview()
  }

  dismissPreview(event) {
    if (event) event.preventDefault()

    this.selectedTargetId = null
    this.selectedDuplicateId = null

    if (this.hasTargetInputTarget) {
      this.targetInputTarget.value = ""
    }
    if (this.hasDuplicateInputTarget) {
      this.duplicateInputTarget.value = ""
    }

    const targetFrame = document.getElementById("target_results")
    if (targetFrame) targetFrame.innerHTML = ""
    
    const duplicateFrame = document.getElementById("duplicate_results")
    if (duplicateFrame) duplicateFrame.innerHTML = ""

    // Remove active classes
    const activeItems = document.querySelectorAll('.deduplication-container .list-group-item.active')
    activeItems.forEach(el => el.classList.remove('active'))

    // Reset preview container
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.innerHTML = `
        <h5 class="text-muted mb-3">Select a Target and a Duplicate to preview the merge</h5>
        <i class="fas fa-object-group fa-3x text-body-tertiary opacity-50"></i>
      `
    }

    this.reloadDuplicatesFrame(null)
  }

  reloadDuplicatesFrame(targetId) {
    const duplicatesFrame = document.getElementById("provider_duplicates_frame")
    if (duplicatesFrame) {
      const baseUrl = this.duplicatesUrlValue
      if (targetId) {
        duplicatesFrame.src = `${baseUrl}?target_id=${targetId}`
      } else {
        duplicatesFrame.src = baseUrl
      }
    }
  }
}
